<script lang="ts" setup>
import CommunityButton from '@/components/common/buttons/CommunityButton.vue'
import { app } from '@/composition/app'
import { MobileScrollContainer } from '@ghl/clientportal-core'
import {
  Home03Icon,
  PlusIcon,
  Settings01Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import { UISkeleton, UITextMdMedium } from '@gohighlevel/ghl-ui'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import draggable from 'vuedraggable-es'
import { GroupState } from '../../../composition/groups'
import { MemberStatus } from '../../../models/Groups'
import { ColorLuminance } from '../../helper'
import ChannelIcon from '../ChannelIcon.vue'

defineProps({
  isLoading: Boolean,
})

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const sidebarHeight = ref(0)
const hoveredChannelSlug = ref()

const childWidth = ref(0)
const channelNameParent = ref<HTMLElement | null>(null)

watch(
  () => channelNameParent.value?.offsetWidth,
  _ => {
    childWidth.value = calculateMaxWidth.value
  }
)

const calculateMaxWidth = computed(() => {
  if (channelNameParent.value) {
    const parentWidth = channelNameParent.value.offsetWidth
    const padding =
      parseFloat(window.getComputedStyle(channelNameParent.value).paddingLeft) +
      parseFloat(window.getComputedStyle(channelNameParent.value).paddingRight)
    return parentWidth - padding - 119
  } else {
    return 200
  }
})

const emit = defineEmits([
  'channel-changed',
  'close-sidenav',
  'open-modal-flag',
  'show-add-channel-modal',
])

function openChannelSetting() {
  emit('open-modal-flag')
  emit('close-sidenav')
}

onMounted(async () => {
  hoveredChannelSlug.value = route.name
  sidebarHeight.value = window.innerHeight
  window.addEventListener('resize', updateHeight)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateHeight)
})

const channelList = computed(() => {
  if (GroupState.state?.userStatus !== MemberStatus.BANNED) {
    return GroupState.state.channelList
  } else {
    return []
  }
})
function updateHeight() {
  sidebarHeight.value = window.innerHeight
}

async function changeChannel(slug: string) {
  hoveredChannelSlug.value = slug
  emit('channel-changed')

  router.push({
    name: 'channel-overview',
    params: {
      channelSlug: slug,
    },
  })
}

function homeTimeline() {
  emit('channel-changed')
  router.push({
    name: 'home',
    params: { groupSlug: route.params.groupSlug },
  })
}
async function addChannel() {
  emit('show-add-channel-modal')
  emit('close-sidenav')
}

// draggable channels
const isDragActive = ref(false)
const activeDragIndex = ref(null)
const isGroupAdmin = computed(() => GroupState.state.isGroupAdmin)
async function elementUpdated(event: any) {
  const { oldIndex, newIndex } = event
  if (oldIndex != newIndex) {
    // rearrange the channel from its old index to new index and update the sequence
    channelList.value.splice(
      newIndex,
      0,
      channelList.value.splice(oldIndex, 1)[0]
    )
    await GroupState.rearrangeChannels(
      channelList.value.map((ch, index) => {
        return { id: ch._id, channelName: ch.name, sequence: index + 1 }
      })
    )
  }
}

const dragOptions = computed(() => ({
  animation: 250,
  disabled: isGroupAdmin.value && !app.state.isMobile ? false : true,
  fallbackOnBody: true,
  swapThreshold: 0.8,
  chosenClass: 'chosen-channel',
  scroll: true,
  forceAutoScrollFallback: true,
}))

const LightColorBg = computed(() => {
  const opacity = 0.97
  return `background-color: ${ColorLuminance(
    getComputedStyle(document.documentElement).getPropertyValue('--primary'),
    opacity
  )}; hover:background-color: ${ColorLuminance(
    getComputedStyle(document.documentElement).getPropertyValue('--primary'),
    opacity
  )}`
})
function startDrag(event) {
  isDragActive.value = true
  activeDragIndex.value = event.oldIndex
}
function endDrag() {
  isDragActive.value = false
  activeDragIndex.value = null
}

const MediumColorBg = computed(() => {
  const opacity = 0.35
  return `color: ${ColorLuminance(
    getComputedStyle(document.documentElement).getPropertyValue('--primary'),
    opacity
  )}`
})
</script>
<template>
  <MobileScrollContainer
    id="side-bar"
    class="sticky w-full overflow-y-auto rounded-xl bg-transparent text-communities-primary"
    :style="{ height: sidebarHeight - 80 + 'px' }"
  >
    <template #scroll-slot>
      <div
        ref="channelNameParent"
        class="relative max-h-full bg-transparent p-3"
      >
        <div
          v-if="GroupState.state.isGroupAdmin && !isLoading"
          class="mb-4 inline-flex w-full flex-col items-start justify-start gap-4"
        >
          <div
            v-if="GroupState.state.isGroupAdmin"
            id="add-channel__btn"
            class="inline-flex h-9 w-full items-center justify-center gap-2 rounded-lg px-3.5 py-2 shadow hover:bg-blend-lighten"
            :style="LightColorBg"
            tabindex="0"
            role="button"
            :aria-label="t('common.ariaLabels.addChannel')"
            @click="addChannel()"
            @keyup.enter="addChannel()"
          >
            <div
              class="relative h-5 w-5 text-communities-sidebar-selected-item-font"
            >
              <PlusIcon class="h-5 pr-1" />
            </div>
            <div
              class="text-sm font-semibold leading-tight text-communities-sidebar-selected-item-font"
            >
              {{ t('channel.addChannel') }}
            </div>
          </div>
        </div>
        <div v-if="!isLoading" class="divider w-full" />
        <div
          v-if="!isLoading"
          class="mb-4 inline-flex w-full flex-col items-start justify-start gap-4"
        >
          <div
            id="home-timeline"
            class="inline-flex cursor-pointer items-center justify-start gap-2 self-stretch rounded-md px-3 py-2 text-communities-sidebar-selected-item-font"
            :class="route.name === 'home' ? 'bg-communities-secondary' : ''"
            :style="route.name === 'home' ? LightColorBg : ''"
            tabindex="0"
            role="button"
            :aria-label="t('common.ariaLabels.home')"
            @click="homeTimeline"
            @keyup.enter="homeTimeline"
          >
            <Home03Icon class="mr-1 h-6 w-6" />

            <UITextMdMedium class="leading-tight">
              {{ t('common.home') }}
            </UITextMdMedium>
          </div>
        </div>
        <div v-if="!isLoading" class="divider w-full" />
        <div
          class="channel-list-scrollbar channel-list revert-box-sizing max-h-full overflow-y-auto"
        >
          <draggable
            v-if="!isLoading && GroupState.state.channelList != undefined"
            v-model="channelList"
            class="content inline-flex w-full flex-col items-start justify-start gap-4"
            v-bind="dragOptions"
            item-key="order"
            @update="elementUpdated"
            @start="startDrag"
            @end="endDrag"
          >
            <template #item="{ element }">
              <div
                :id="element.slug"
                :key="element.slug"
                class="inline-flex cursor-pointer items-center justify-start gap-2 self-stretch rounded-md px-3 py-2 text-communities-sidebar-selected-item-font"
                :class="
                  route.params.channelSlug === element.slug &&
                  'bg-communities-secondary'
                "
                :style="
                  route.params.channelSlug === element.slug ? LightColorBg : ''
                "
                tabindex="0"
                role="button"
                :aria-label="`${element.name} Channel`"
                @click="changeChannel(element.slug)"
                @keyup.enter="changeChannel(element.slug)"
                @mouseenter="hoveredChannelSlug = element.slug"
                @mouseleave="hoveredChannelSlug = null"
                @focus="hoveredChannelSlug = element.slug"
                @blur="hoveredChannelSlug = null"
              >
                <div
                  class="relative flex h-6 shrink grow basis-0 items-center justify-start gap-3"
                >
                  <ChannelIcon
                    :channel="element"
                    :icon-size="20"
                    :lock-icon-size="'h-3.5 w-3.5'"
                    :emoji-size="20"
                    :lock-icon-position="{ top: 1, right: -1 }"
                    :lock-icon-emoji-position="{ top: 4, right: -1 }"
                    :lock-icon-fill="'var(--fontPrimary)'"
                    :lock-icon-css="'bg-communities-background rounded-full'"
                    :showing-in-mobile-drawer="true"
                  />
                  <UITextMdMedium
                    :id="`${element.name}-channel-name`"
                    class="ml-2 overflow-hidden text-ellipsis whitespace-nowrap leading-tight text-communities-sidebar-selected-item-font"
                    :style="`width:${childWidth}px`"
                  >
                    {{ element.name }}
                  </UITextMdMedium>

                  <div
                    v-if="element.unreadCount > 0"
                    :id="`${element._id}-unread-count`"
                    class="order-last col-span-1 flex h-[20px] w-7 cursor-pointer items-center justify-center rounded-xl border-none bg-communities-sidebar-selected-item-font"
                  >
                    <span class="text-xs font-medium" :style="MediumColorBg">
                      {{
                        element.unreadCount > 99 ? '99+' : element.unreadCount
                      }}
                    </span>
                  </div>
                  <CommunityButton
                    v-if="route.params.channelSlug === element.slug"
                    id="edit_channel_btn"
                    button-type="text"
                    :circle="true"
                    class="order-last col-span-1 flex h-5 w-5 cursor-pointer items-center justify-center border-none !bg-transparent text-communities-primary"
                    @click="openChannelSetting"
                  >
                    <Settings01Icon
                      id="edit_channel_btn_icon"
                      class="h-5 w-5 !text-communities-sidebar-selected-item-font"
                    />
                  </CommunityButton>
                </div>
              </div>
            </template>
          </draggable>
          <div v-else>
            <div
              v-for="index in 5"
              :key="index"
              class="mt-2 inline-flex cursor-pointer items-center justify-start gap-2 self-stretch rounded-md px-3 py-2 text-communities-sidebar-selected-item-font"
            >
              <div
                class="relative flex h-6 shrink grow basis-0 items-center justify-start gap-3"
              >
                <UISkeleton height="20px" circle />
                <UISkeleton
                  class="ml-2"
                  :style="`width:${childWidth}px`"
                  round
                  height="20px"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </MobileScrollContainer>
</template>

<style scoped>
.radio-style {
  width: 200px;
  max-width: 300px;
}

#radio-style:checked {
  background-color: #3b82f6;
}

.channel-list-scrollbar {
  scrollbar-width: thin;
}

.channel-list-scrollbar::-webkit-scrollbar {
  width: 0px;
}

.channel-list-scrollbar:hover::-webkit-scrollbar {
  width: 4px;
}

.channel-list-scrollbar::-webkit-scrollbar-thumb {
  background-color: #c2c2c2;
  border-radius: 2px;
}

#side-bar::-webkit-scrollbar {
  width: 0px;
}
/* .revert-box-sizing {
  box-sizing: revert !important;
} */

.divider {
  margin-top: 0px !important;
  margin-bottom: 16px !important;
  border-top: var(--border-style) #344054;
}

.channel-list {
  height: 100% !important;
}
.chosen-channel {
  border-bottom: 3px solid green;
}
</style>
